/**
 * PublicidadApiClient — generación vía n8n webhook `generar-publicidad-negocio`
 * (instrucción INS-PUBLICIDAD-001 en nexo_catalogs.n8n_instructions).
 *
 * 1) Guarda borrador en NexoAPI (historial).
 * 2) POST a n8n con payload completo (negocio + tipo + variables + marca).
 *
 * @module infrastructure/api/PublicidadApiClient
 */
import { AppConfig } from '../../config.js';
import { ApiBase } from './ApiBase.js';
import { ok, fail } from '../../domain/value-objects/CrudResult.js';

/** Mismo patrón que generación de fondo: webhook dedicado en n8n. */
export const N8N_PUBLICIDAD_WEBHOOK_PATH = '/webhook/generar-publicidad-negocio';

export const PublicidadApiClient = {
  /**
   * @param {object} opts
   * @param {string} opts.businessId
   * @param {string} [opts.businessName]
   * @param {string} [opts.businessType]
   * @param {string} opts.tipoPublicidad oferta|servicio|marca|campana|interaccion|informativa
   * @param {string} [opts.instrucciones]
   * @param {object} [opts.variables]
   * @param {string} [opts.logoUrl]
   * @param {string} [opts.colorPrimario]
   * @param {string} [opts.colorSecundario]
   * @param {string} [opts.colorTexto]
   * @param {string} [opts.personajeUrl]
   * @param {string} [opts.personajeCategoria]
   * @param {string} [opts.fontName]
   * @param {string} [opts.fontUrl]
   * @param {string} [opts.instructionId] override catálogo (default INS-PUBLICIDAD-001)
   */
  async generate({
    businessId,
    businessName,
    businessType,
    tipoPublicidad,
    instrucciones,
    variables,
    logoUrl,
    colorPrimario,
    colorSecundario,
    colorTexto,
    personajeUrl,
    personajeCategoria,
    fontName,
    fontUrl,
    instructionId,
  }) {
    const saveRes = await ApiBase.post('/api/v1/publicidad', {
      businessId,
      tipoPublicidad,
      instrucciones,
      variables,
      estado: 'pendiente',
    });

    const apiPayload = saveRes.data;
    const root = apiPayload?.data ?? apiPayload;
    const pubId = root?.id;
    if (!saveRes.ok || !pubId) {
      return fail(saveRes.code || saveRes.status || 'SAVE_ERR', saveRes.error || 'Error guardando borrador en BD');
    }

    const url = `${AppConfig.n8nBase}${N8N_PUBLICIDAD_WEBHOOK_PATH}`;
    const body = {
      publicidad_id: pubId,
      business_id: businessId,
      business_name: businessName || '',
      business_type: businessType || '',
      tipo_publicidad: tipoPublicidad,
      instrucciones: instrucciones || '',
      variables: variables && typeof variables === 'object' ? variables : {},
      logo_url: logoUrl || '',
      color_primario: colorPrimario || '',
      color_secundario: colorSecundario || '',
      color_texto: colorTexto || '',
      personaje_url: personajeUrl || '',
      personaje_categoria: personajeCategoria || 'marca',
      font_name: fontName || '',
      font_url: fontUrl || '',
      instruction_id: instructionId || 'INS-PUBLICIDAD-001',
    };

    try {
      const res = await ApiBase.fetchQueCambia(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const raw = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = raw?.message || raw?.detail || raw?.error || `Error N8N HTTP ${res.status}`;
        return fail(String(res.status), msg, raw);
      }
      const inner = raw?.data !== undefined ? raw.data : raw;
      if (inner?.success === false && inner?.error) {
        return fail('N8N_ERR', inner.error, inner);
      }
      return ok(inner);
    } catch (err) {
      return fail('NETWORK', err?.message || 'Error de red al contactar n8n');
    }
  },

  async list(businessId, page = 1, pageSize = 20) {
    return ApiBase.get(`/api/v1/publicidad?businessId=${businessId}&page=${page}&pageSize=${pageSize}`);
  },

  async save(businessId, data) {
    return ApiBase.post('/api/v1/publicidad', { businessId, ...data });
  },
};
